import { c as _c } from "react/compiler-runtime";
import { FC, MouseEvent, ReactNode } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import styled, { css, useTheme } from 'styled-components';
import { copyTextToClipboard } from '../../../utils/copy';
import { Div } from '../helpers/StyledUtils';
import { useToasts } from '../Toast';
import Button from './Button';
import Icon, { IconName } from './Icon';
import Text from './Text';
const StyledTooltip = styled.div<{
  max_width?: number;
}>(({
  theme,
  max_width = 344
}) => css`
    max-width: ${max_width}px;
    word-break: break-word;
    white-space: initial;
    width: max-content;
    background-color: ${theme.colors.surface.base.surface};
    box-shadow: ${theme.elevation[3]};
    border-radius: ${theme.radius.normal};
    padding: ${theme.spacing(2)};
  `);
export type TooltipCTA = {
  icon?: IconName;
  onClick?: () => void;
  label: string;
  to?: string;
};

// New Tooltip component based on Radix Tooltip but keeping the same API
const Tooltip: FC<{
  open?: boolean;
  align?: 'right' | 'left' | 'center';
  disabled?: boolean;
  side?: 'right' | 'bottom' | 'left' | 'top';
  tooltip?: ReactNode;
  children: ReactNode;
  copyable?: boolean;
  offset?: [number, number];
  fill?: boolean;
  cta?: TooltipCTA;
  max_width?: number;
  as?: React.ElementType;
  element?: ReactNode;
  onHover?: () => void;
  onLeave?: () => void;
  delay?: number;
}> = t0 => {
  const $ = _c(48);
  const {
    open,
    tooltip,
    side: t1,
    align: t2,
    children,
    copyable,
    disabled,
    cta,
    offset,
    fill,
    max_width,
    as,
    element: t3,
    onHover,
    onLeave,
    delay
  } = t0;
  const side = t1 === undefined ? "right" : t1;
  const align = t2 === undefined ? "end" : t2;
  const element = t3 === undefined ? null : t3;
  const {
    addToast
  } = useToasts();
  const theme = useTheme();
  let t4;
  if ($[0] !== cta?.to) {
    t4 = cta?.to?.startsWith("http") || false;
    $[0] = cta?.to;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const cta_link_is_external = t4;
  let t5;
  if ($[2] !== addToast || $[3] !== tooltip) {
    t5 = e => {
      e.stopPropagation();
      copyTextToClipboard(String(tooltip)).then(success => success ? addToast("success", `Copied '${String(tooltip)}' to clipboard`) : addToast("error", "Failed to copy"));
    };
    $[2] = addToast;
    $[3] = tooltip;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleCopyToClipboard = t5;
  const t6 = delay ?? 50;
  const t7 = disabled === true ? false : open;
  let t8;
  if ($[5] !== onHover || $[6] !== onLeave) {
    t8 = onHover || onLeave ? open_0 => {
      if (open_0) {
        onHover?.();
      } else {
        onLeave?.();
      }
    } : undefined;
    $[5] = onHover;
    $[6] = onLeave;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = fill ? "block" : "inline-flex";
  const t10 = fill ? "100%" : "fit-content";
  let t11;
  if ($[8] !== t10 || $[9] !== t9) {
    t11 = {
      display: t9,
      width: t10
    };
    $[8] = t10;
    $[9] = t9;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== as || $[12] !== children || $[13] !== t11) {
    t12 = <Div as={as} style={t11}>{children}</Div>;
    $[11] = as;
    $[12] = children;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== disabled || $[16] !== t12) {
    t13 = <RadixTooltip.Trigger disabled={disabled} asChild={true}>{t12}</RadixTooltip.Trigger>;
    $[15] = disabled;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== theme.zindex.tooltip) {
    t14 = {
      zIndex: theme.zindex.tooltip
    };
    $[18] = theme.zindex.tooltip;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const t15 = align === "center" ? "center" : align === "left" ? "end" : "start";
  const t16 = offset ? offset[1] : 6;
  const t17 = offset ? offset[0] : 0;
  const t18 = copyable ? handleCopyToClipboard : undefined;
  const t19 = copyable ? "pointer" : "default";
  let t20;
  if ($[20] !== t19) {
    t20 = {
      cursor: t19
    };
    $[20] = t19;
    $[21] = t20;
  } else {
    t20 = $[21];
  }
  let t21;
  if ($[22] !== copyable || $[23] !== cta || $[24] !== cta_link_is_external || $[25] !== element || $[26] !== tooltip) {
    t21 = element || <><Div flex={{
        justify: "space-between",
        gap: 2,
        align: "center"
      }}><Text size="s">{tooltip}</Text>{copyable && <Icon icon="copy" right={true} pointer={true} />}</Div>{cta && <Button as={cta_link_is_external ? "a" : undefined} block={true} outline={true} target={cta_link_is_external ? "_blank" : undefined} to={cta_link_is_external ? undefined : cta.to} href={cta.to} icon={cta.icon || "docs"} onClick={cta.onClick} m={{
        t: 3
      }}>{cta.label}</Button>}</>;
    $[22] = copyable;
    $[23] = cta;
    $[24] = cta_link_is_external;
    $[25] = element;
    $[26] = tooltip;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== max_width || $[29] !== t18 || $[30] !== t20 || $[31] !== t21) {
    t22 = <StyledTooltip max_width={max_width} onClick={t18} style={t20}>{t21}</StyledTooltip>;
    $[28] = max_width;
    $[29] = t18;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== side || $[34] !== t14 || $[35] !== t15 || $[36] !== t16 || $[37] !== t17 || $[38] !== t22) {
    t23 = <RadixTooltip.Portal><RadixTooltip.Content style={t14} align={t15} side={side} sideOffset={t16} alignOffset={t17}>{t22}</RadixTooltip.Content></RadixTooltip.Portal>;
    $[33] = side;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t13 || $[41] !== t23 || $[42] !== t7 || $[43] !== t8) {
    t24 = <RadixTooltip.Root open={t7} onOpenChange={t8}>{t13}{t23}</RadixTooltip.Root>;
    $[40] = t13;
    $[41] = t23;
    $[42] = t7;
    $[43] = t8;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t24 || $[46] !== t6) {
    t25 = <RadixTooltip.Provider delayDuration={t6}>{t24}</RadixTooltip.Provider>;
    $[45] = t24;
    $[46] = t6;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  return t25;
};
export default Tooltip;